import React, { useState } from 'react';
import { exportAndDownloadExcel, getFormSubList } from '../api/forms';
import toastManager from '../utils/toastManager';

const ExportExcelModal = ({ formId, visible, onClose }) => {
  const [exporting, setExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    startDate: '',
    endDate: '',
    limit: 10000,
    includeHeaders: true
  });

  // 格式化时间为年月日
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toISOString().split('T')[0];
    } catch (e) {
      return dateString;
    }
  };

  // 格式化字段值（与页面显示逻辑一致）
  const formatFieldValue = (value) => {
    if (value && typeof value === 'object' && value.brand && value.store) {
      return `${value.brand}${value.store ? '-' + value.store : ''}`;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return value || '';
  };

  const handleExport = async () => {
    if (!formId) {
      toastManager.error('表单ID不能为空');
      return;
    }

    setExporting(true);
    try {
      // 获取所有数据（不分页）
      const res = await getFormSubList(formId, { 
        page: 1, 
        size: exportOptions.limit || 10000 
      });
      
      const records = res.data?.list || [];
      
      if (records.length === 0) {
        toastManager.error('没有数据可导出');
        return;
      }

      // 解析所有 submissionData
      const parsedRecords = records.map(rec => ({
        ...rec,
        parsedData: rec.submissionData ? JSON.parse(rec.submissionData) : {}
      }));

      // 获取所有字段名（与页面逻辑一致）
      const allKeys = Array.from(
        new Set(parsedRecords.flatMap(rec => Object.keys(rec.parsedData)))
      );

      // 构建Excel数据
      const excelData = [];
      
      // 表头
      if (exportOptions.includeHeaders) {
        const headers = ['序号', '微信昵称', ...allKeys, '提交时间'];
        excelData.push(headers);
      }

      // 数据行
      parsedRecords.forEach((rec, idx) => {
        const row = [
          idx + 1, // 序号
          rec.nickName || '-', // 微信昵称
          ...allKeys.map(key => formatFieldValue(rec.parsedData[key])), // 动态字段
          formatDate(rec.submittedAt || rec.createdAt || rec.created_at || '') // 提交时间
        ];
        excelData.push(row);
      });

      // 生成Excel文件
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '表单数据');
      
      // 下载文件
      const fileName = `表单填写记录_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      toastManager.success('导出成功');
      onClose();
    } catch (error) {
      console.error('导出失败:', error);
      toastManager.error('导出失败，请重试');
    } finally {
      setExporting(false);
    }
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '32px',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#222' }}>
            📊 导出Excel
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#999',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
            开始日期
          </label>
          <input
            type="date"
            value={exportOptions.startDate}
            onChange={(e) => setExportOptions(prev => ({ ...prev, startDate: e.target.value }))}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
            结束日期
          </label>
          <input
            type="date"
            value={exportOptions.endDate}
            onChange={(e) => setExportOptions(prev => ({ ...prev, endDate: e.target.value }))}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
            导出数量限制
          </label>
          <select
            value={exportOptions.limit}
            onChange={(e) => setExportOptions(prev => ({ ...prev, limit: parseInt(e.target.value) }))}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value={1000}>1000条</option>
            <option value={5000}>5000条</option>
            <option value={10000}>10000条</option>
            <option value={50000}>50000条</option>
            <option value={100000}>100000条</option>
          </select>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={exportOptions.includeHeaders}
              onChange={(e) => setExportOptions(prev => ({ ...prev, includeHeaders: e.target.checked }))}
              style={{ marginRight: '8px' }}
            />
            <span style={{ fontSize: '14px', color: '#333' }}>包含表头</span>
          </label>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              background: '#fff',
              color: '#666',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            取消
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            style={{
              padding: '10px 24px',
              border: 'none',
              borderRadius: '6px',
              background: exporting ? '#ccc' : '#1890ff',
              color: '#fff',
              cursor: exporting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {exporting ? '导出中...' : '开始导出'}
          </button>
        </div>

        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: '#f6ffed',
          border: '1px solid #b7eb8f',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#389e0d'
        }}>
          💡 提示：导出大量数据可能需要较长时间，请耐心等待
        </div>
      </div>
    </div>
  );
};

export default ExportExcelModal; 